
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useMaska } from '@/common/composable/useMaska'

// constants
import { SHIPMENT_TYPE_OPTIONS, WARNING_MESSAGES } from '@/core/constants/calculation.constants'

// realisations
import { billing } from '@/core/realisations/billing.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { invoices } from '@/core/realisations/invoices.realisation'
import { orders } from '@/core/realisations/orders.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { CreateInternationalCalculationValidation } from '@/modules/BillingModule/validations/CreateCalculation.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'
import { HistoryInvoiceT } from '@/core/types/invoices.types'
import { InternationalCalculationPayloadT } from '@/core/types/billing.types'

// masks
import PRICE_MASK from '@/common/masks/price.mask'

// composable
import useHelpers from '@/common/composable/useHelpers'
import { useRouter } from 'vue-router'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
    },
    setup() {
        const notifications = useNotifications()
        const { unmaskNumbers } = useMaska()
        const { findObjectByValue } = useHelpers()
        const router = useRouter()

        const state = reactive({
            invoiceNumber: null as string | null,
            companyId: null as number | null,
            shipmentTypeId: null as number | null,
            deliveryCharge: null as string | null,
            //
            invoiceId: null as number | null,
            orderCreatedAt: null as string | null,
            deliveredAt: null as string | null,
            direction: null as string | null,
            orderNumber: null as string | null,
            weight: null as number | null,
            volumeWeight: null as number | null,
        })

        const progress = reactive({
            is_loading: false,
        })

        const state_form$ = useVuelidate(CreateInternationalCalculationValidation, state)

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            invoices: new DataState<HistoryInvoiceT>({}),
        })

        //

        const loadInvoices = async (page: number, search?: string) => {
            data_state.invoices.pagination.current_page = 1

            try {
                data_state.invoices.is_loading = true

                const params = {
                    page,
                    limit: 50,
                    search: search || null,
                    isInternational: 1,
                }

                const data = await invoices.getListInternational(params)
                data_state.invoices.list = data.data

                data_state.invoices.pagination.total_pages = data.meta.last_page

                data_state.invoices.is_failed = false
                data_state.invoices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.invoices.failure.catchError(error)

                if (!data_state.invoices.failure.error_state.is_cancel) {
                    data_state.invoices.is_failed = true
                }
            } finally {
                if (!data_state.invoices.failure.error_state.is_cancel) {
                    data_state.invoices.is_loading = false
                }
            }
        }

        const loadMoreInvoices = async (page: number) => {
            if (page > data_state.invoices.pagination.total_pages) return
            data_state.invoices.pagination.current_page = page

            try {
                data_state.invoices.is_loading = true

                const params = {
                    page,
                    limit: 50,
                    isInternational: 1,
                }

                const response = await invoices.getListInternational(params)
                data_state.invoices.list = data_state.invoices.list.concat(response.data)

                data_state.invoices.pagination.total_pages = response.meta.last_page

                data_state.invoices.is_failed = false
                data_state.invoices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.invoices.failure.catchError(error)

                if (!data_state.invoices.failure.error_state.is_cancel) {
                    data_state.invoices.is_failed = true
                }
            } finally {
                if (!data_state.invoices.failure.error_state.is_cancel) {
                    data_state.invoices.is_loading = false
                }
            }
        }

        const searchInvoice = (search: string) => {
            loadInvoices(1, search)
        }

        const unshiftFindedInvoice = async (invoice: HistoryInvoiceT) => {
            await loadInvoices(data_state.invoices.pagination.current_page)
            data_state.invoices.list.unshift(invoice)
        }

        //

        const loadCompanies = async (search?: string) => {
            data_state.companies.pagination.current_page = 1

            try {
                data_state.companies.is_loading = true

                const data = await companies.getList({
                    page: 1,
                    limit: 9999,
                    billingEnabled: 1,
                    search,
                })
                data_state.companies.list = data.data

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompany = (search: string) => {
            loadCompanies(search)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies()
            data_state.companies.list.unshift(company)
        }

        //

        const getPayload = (): InternationalCalculationPayloadT => {
            const deliveryCharge = state.deliveryCharge
                ? Number(unmaskNumbers(state.deliveryCharge))
                : null

            const payload: InternationalCalculationPayloadT = {
                invoiceNumber: state.invoiceNumber as string,
                companyId: state.companyId as number,
                shipmentTypeId: state.shipmentTypeId as number,
                invoiceId: state.invoiceId as number,
                orderCreatedAt: state.orderCreatedAt,
                deliveredAt: state.deliveredAt,
                direction: state.direction as string,
                weight: state.weight as number,
                volumeWeight: state.volumeWeight as number,
                deliveryCharge: deliveryCharge as number,
            }

            if (state.orderNumber) {
                payload.orderNumber = state.orderNumber
            }

            return payload as InternationalCalculationPayloadT
        }

        const triggerWarning = (title: string) => {
            notifications.notify({
                title,
                type: 'danger',
            })
        }

        const isError = computed(() => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return true

            if (!state.invoiceNumber) triggerWarning(WARNING_MESSAGES['invoiceNumber'])
            if (!state.deliveredAt) {
                triggerWarning(WARNING_MESSAGES['notDelivered'])
                return true
            }

            return false
        })

        const onSubmit = async () => {
            if (isError.value) return

            try {
                progress.is_loading = true

                await billing.createInternationalCalculation(getPayload())

                notifications.notify({
                    title: 'Создается запись по вычислению...',
                    type: 'success',
                })
                router.push('/billing')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании записи произошла ошибка',
                })
            } finally {
                progress.is_loading = false
            }
        }

        //

        const clearState = () => {
            state.companyId = null
            state.shipmentTypeId = null
            state.invoiceId = null
            state.orderCreatedAt = null
            state.deliveredAt = null
            state.direction = null
            state.orderNumber = null
            state.weight = null
            state.volumeWeight = null
            state.deliveryCharge = null
        }

        const onInvoiceChange = async (invoice_number: string) => {
            clearState()

            const selected_invoice = findObjectByValue(
                'invoice_number',
                invoice_number,
                data_state.invoices.list
            )
            if (!selected_invoice) return

            try {
                progress.is_loading = true

                const data = await orders.getInvoice(selected_invoice.id)
                const invoice = data.data

                if (!invoice) return

                state.companyId = invoice.company_id
                state.shipmentTypeId = invoice.shipment_type_id

                state.invoiceId = invoice.id
                state.orderCreatedAt = invoice.created_at
                state.deliveredAt = invoice.delivery_date
                state.direction = invoice.direction
                state.orderNumber = invoice.order_number
                state.weight = invoice.weight
                state.volumeWeight = invoice.volume_weight
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При загрузке накладной произошла ошибка',
                    type: 'danger',
                })
            } finally {
                progress.is_loading = false
            }
        }

        //

        onBeforeMount(() => {
            loadInvoices(1)
            loadCompanies()
        })

        //

        return {
            state,
            progress,
            state_form$,
            data_state,
            //
            onSubmit,
            onInvoiceChange,
            //
            loadInvoices,
            loadMoreInvoices,
            searchInvoice,
            unshiftFindedInvoice,
            //
            loadCompanies,
            searchCompany,
            unshiftFindedCompany,
            //
            PRICE_MASK,
            SHIPMENT_TYPE_OPTIONS,
        }
    },
})
